<template>
  <div class="p-6 text-gray-900 bg-white rounded-lg text-left font-medium shadow-lg">
    <h1 class="font-semibold text-xl">
      <fa-icon
        :icon="['fas', 'upload']"
        size="lg"
        class="mr-1 text-blue-400"
      />
      {{ $t("Upload sentiment rules", "Upload sentiment rules") }}
      <span class="text-gray-500">
        / {{ fileName }}
      </span>
    </h1>
    <progress-bar
      v-if="isUploading"
      :all="selectedSheet.length - header"
      :completed="finishedCount"
      class="mt-4"
    />
    <div v-else>
      <div class="mt-4 flex">
        <div
          v-for="(sheetName, index) in Object.keys(workbook)"
          :key="index"
          class="rounded-t py-2 px-4 w-1/6 mr-2"
          :class="isSelected === index ? 'text-blue-400 bg-gray-100 cursor-default' : 'text-gray-600 bg-gray-200 cursor-pointer'"
          @click="selectSheet(index)"
        >
          <fa-icon
            v-if="isSelected === index"
            :icon="['fas', 'scrubber']"
            size="sm"
            class="mr-1 text-blue-400"
          />
          {{ sheetName }}
        </div>
      </div>
      <div class="-mt-2 p-4 bg-gray-100 w-full h-full font-normal flex overflow-auto overflow-x-auto">
        <div :style="headerSetterStyle">
          <fa-icon
            v-tooltip="$t('header.up', 'Move header upwards')"
            :icon="['fal', 'angle-up']"
            size="lg"
            class="text-gray-500 cursor-pointer hover:text-gray-700 block"
            @click="changeHeader('up')"
          />
          <fa-icon
            v-tooltip="$t('header.down', 'Move header downwards')"
            :icon="['fal', 'angle-down']"
            size="lg"
            class="text-gray-500 cursor-pointer hover:text-gray-700 block"
            @click="changeHeader('down')"
          />
        </div>
        <table v-if="selectedSheet">
          <tr
            v-if="selectedSheet[0]"
            class="w-full"
          >
            <td
              v-for="n in selectedSheet[0].length"
              :key="n"
              class="mx-2"
            >
              <select
                :value="columnTypeValues[n-1]"
                class="p-2 mb-4 bg-gray-100 border-b-2 border-gray-500"
                @change="changeColumnType(n-1, $event.target.value)"
              >
                <option
                  v-for="(columnType, typeIndex) in columnTypes"
                  :key="typeIndex"
                  :value="typeIndex"
                >
                  {{ columnType }}
                </option>
              </select>
            </td>
          </tr>
          <tr
            v-for="(row, rowIndex) in selectedSheet"
            :key="rowIndex"
            class="border-b-2"
            :class="header === (rowIndex + 1) ? 'border-blue-400' : 'border-gray-200'"
          >
            <td
              v-for="(cell, columnIndex) in row"
              :key="columnIndex"
              class="p-2"
            >
              {{ cell }}
            </td>
          </tr>
        </table>
      </div>
      <div class="w-full mt-4 flex justify-end">
        <span
          v-if="selectedSheet"
          class="py-2 font-normal"
        >
          {{ selectedSheet.length - header }} {{ $t("Lines", "lines") }}
        </span>
        <z-button
          type="primary"
          :text="$t('Upload', 'Upload')"
          @click="upload"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCircle, faPlusCircle, faAngleDown, faAngleUp } from '@fortawesome/pro-light-svg-icons'
import { faUpload, faScrubber } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { actionNames } from '@/sentiment-rules/store'
import ZButton from '@/global/ui/Button'
import PQueue from 'p-queue'
import { mapState } from 'vuex'
import ProgressBar from '@/global/ui/ProgressBar'

library.add(faCircle, faPlusCircle, faAngleDown, faAngleUp, faUpload, faScrubber)
export default {
  name: 'UploadedRules',
  components: {
    ProgressBar,
    'fa-icon': FontAwesomeIcon,
    ZButton
  },
  props: {
    fileName: { type: String, default: '' },
    workbook: { type: Object, default: () => {} }
  },
  data () {
    return {
      isUploading: false,
      finishedCount: 0,
      isSelected: 0,
      selectedSheet: [],
      header: 1,
      columnTypeValues: [1, 2, 3, 4, 5],
      columnContentTypes: [],
      variableColumns: {
        phrase: 0,
        value: 1,
        stemmed: 2,
        lang: 3,
        project: 4
      },
      phraseCount: 1,
      valueCount: 1,
      stemmedCount: 1,
      langCount: 1
    }
  },
  computed: {
    ...mapState({
      projects: state => state.projects.projects
    }),
    columnTypes () {
      return [
        this.$t('Column type not used', 'Don\'t use'),
        this.$t('Column type phrase', 'Phrase'),
        this.$t('Column type value', 'Value'),
        this.$t('Column type stemmed', 'Stemmed/Exact match'),
        this.$t('Column type lang', 'Language'),
        this.$t('Column type project', 'Project')
      ]
    },
    headerSetterStyle () {
      const h = this.header * 42 + 36
      return 'margin-top:' + h + 'px'
    }
  },
  async mounted () {
    this.selectSheet(0)
  },
  methods: {
    selectSheet (i) {
      this.isSelected = i
      this.selectedSheet = this.workbook[Object.keys(this.workbook)[i]]
      this.setColumnTypesForAllColumns()
      this.setContentTypesForAllColumns()
    },
    setColumnTypesForAllColumns () {
      // Set 'dont use' for all columns after the first 5 columns
      for (var c = 0; c < (this.selectedSheet[0].length - this.columnTypeValues.length); c++) {
        this.columnTypeValues.push(0)
      }
    },
    setContentTypesForAllColumns () {
      for (let column = 0; column < this.selectedSheet[0].length; column++) {
        for (let row = 0; row < this.selectedSheet.length; row++) {
          if (row < this.header) {
            continue
          }
          const type = isNaN(parseInt(this.selectedSheet[row][column])) ? 'string' : 'number'
          if (!this.columnContentTypes[column]) {
            this.columnContentTypes[column] = type
          } else if (this.columnContentTypes[column] !== type) {
            this.$toast.error(this.$t('There are multiple types in one column', 'There are multiple type data in some columns.'))
          }
        }
      }
    },
    changeHeader (direction) {
      if (direction === 'up') {
        this.header--
      } else if (direction === 'down') {
        this.header++
      }
      this.setContentTypesForAllColumns()
    },
    changeColumnType (index, newTypeId) {
      if (this.hasMatchingTypes(index, newTypeId)) {
        this.columnTypeValues = this.columnTypeValues.map((typeId, i) => {
          return index === i ? parseInt(newTypeId) : typeId
        })
        this.refreshVariables()
      }
    },
    hasMatchingTypes (index, newTypeId) {
      const contentType = this.columnContentTypes[index]
      const typeId = parseInt(newTypeId)
      let columnType = 'string'
      if (typeId === 2 || typeId === 3) {
        columnType = 'number'
      }
      if (contentType !== columnType) {
        this.$toast.error(this.$t('Column type and content type mismatch', 'You cannot set this type for this column.'))
        return false
      }
      return true
    },
    refreshVariables () {
      this.phraseCount = 0
      this.valueCount = 0
      this.stemmedCount = 0
      this.langCount = 0
      this.projectCount = 0
      this.variableColumns = {
        phrase: 0,
        value: 1,
        stemmed: 2,
        lang: 3,
        project: undefined
      }
      for (let c = 0; c < this.columnTypeValues.length; c++) {
        switch (this.columnTypeValues[c]) {
          case 1:
            this.variableColumns.phrase = c
            this.phraseCount++
            break
          case 2:
            this.variableColumns.value = c
            this.valueCount++
            break
          case 3:
            this.variableColumns.stemmed = c
            this.stemmedCount++
            break
          case 4:
            this.variableColumns.lang = c
            this.langCount++
            break
          case 5:
            this.variableColumns.project = c
            this.projectCount++
            break
        }
      }
    },
    async upload () {
      const queue = new PQueue({ concurrency: 2 })
      if (this.phraseCount !== 1 || this.valueCount !== 1 || this.stemmedCount !== 1 || this.langCount !== 1 || this.projectCount > 1) {
        this.$toast.error(this.$t('Incorrect column types', 'Please check if you have exactly one Phrase, Value, Stemmed/Exact match and Language column and maximum 1 Project column.'))
        return
      }
      this.isUploading = true
      this.selectedSheet
        .slice(this.header)
        .forEach(row => queue.add(() =>
          this.$store.dispatch(actionNames.ADD_RULE, {
            phrase: row[this.variableColumns.phrase].trim(),
            score: parseInt(row[this.variableColumns.value]),
            stemmed: parseInt(row[this.variableColumns.stemmed]),
            lang: row[this.variableColumns.lang],
            projects: this.getProjectIdsForRow(row),
            shouldAdd: false
          }).then(() => this.finishedCount++)
            .catch(() => this.$toast.error(`Could not upload sentiment rule: ${row[this.variableColumns.phrase]}`))
        ))
      await queue.onEmpty()
      this.isUploading = false
      this.$emit('finished')
      this.$toast.success(this.$t('Sentiment Rules uploaded', 'Sentiment Rules uploaded'))
    },
    getProjectIdsForRow (row) {
      if (this.variableColumns.project === undefined || !row[this.variableColumns.project]) {
        return []
      }
      return row[this.variableColumns.project].split(',')
        .map(projectName => projectName.trim())
        .map(projectName => this.projects.find(p => p.name === projectName))
        .filter(project => !!project)
        .map(project => project.id)
    }
  }
}
</script>
